import { doAdminQueries, IQueryInfo } from 'infrastructure/api'
import { atom } from 'infrastructure/state/jotai.wrapper'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { sharedQueryFields } from 'shared/api/utility'

export const singleResourceIdAtom = atom<{
  resourceKey?: string
  id?: string | number
}>({})

const resourceMap = {
  partners: {
    key: 'getPartner',
    fields: sharedQueryFields.partner,
  },
  products: {
    key: 'getProduct',
    fields: sharedQueryFields.product,
  },
  productGroups: {
    key: 'getGroupByID',
    fields: sharedQueryFields.productGroups,
  },
}

export type ResourceKey = keyof typeof resourceMap

function isResourceKey(key?: string): key is ResourceKey {
  return !!key && key in resourceMap
}

export function useSingleResource<T>() {
  const [item, setItem] = useState<T | undefined>(undefined)
  const { id, resourceKey } = useAtomValue(singleResourceIdAtom)

  const fetchFunc = useCallback(async () => {
    if (!isResourceKey(resourceKey) || !id) {
      setItem(undefined)
      return
    }
    const queryData = resourceMap[resourceKey]
    const query: IQueryInfo = {
      resourceData: queryData,
      params: { id },
    }
    const { data } = await doAdminQueries<T>([query])
    const item = data[queryData.key]
    setItem(item)
  }, [id, resourceKey])

  useEffect(() => {
    fetchFunc().then()
  }, [fetchFunc])

  return useMemo(() => {
    return {
      reFetch: fetchFunc,
      id,
      item,
    }
  }, [id, item, fetchFunc])
}
